import { useEffect, useState } from "react";
import FocusLock from "react-focus-lock";
import { SocialMediaShareButton } from "~/components/SocialMediaShareButton/SocialMediaShareButton";
import {
  getFacebookUrl,
  getLinkedInUrl,
  getShareEmailUrl,
  getTwitterUrl,
} from "~/lib/client/getSocialMediaUrl";
import { Headline, Icon, IconButton } from "~/ui-library";
import { Flyout } from "~/ui-library/components/Flyout/Flyout";
import {
  EmailIcon,
  FacebookIcon,
  LinkedInIcon,
  TwitterIcon,
  XMarkIcon,
} from "~/ui-library/icons";

type ButtonRenderProps = {
  onClick?: () => void;
};

export type SocialMediaShareFlyoutProps = {
  /** Component to render the button which opens the flyout */
  renderButton: React.FC<ButtonRenderProps>;
  /** Title to display at the start of the share post */
  shareTitle: string;
  /** Url to display at the end of the share post */
  shareUrl: string;
};

export const SocialMediaShareFlyout: React.FC<SocialMediaShareFlyoutProps> = ({
  shareTitle,
  shareUrl,
  renderButton,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [isOpen]);

  return (
    <>
      {renderButton({ onClick: onOpen })}
      <Flyout
        openFromDirection="bottom"
        fillScreen={false}
        isOpen={isOpen}
        closeOnOverlayClick
        onClose={onClose}
      >
        <FocusLock disabled={!isOpen}>
          <div className="flex flex-col border-1 border-gray-300">
            <div className="flex w-full items-center justify-between px-s py-xs">
              <Headline blockCase level={6}>
                Share to
              </Headline>

              <IconButton
                aria-label="Close the share menu"
                onClick={onClose}
                size="default"
                SvgIcon={XMarkIcon}
                variant="subtle"
              />
            </div>

            <hr />

            {/* Container for individual buttons  */}
            <div className="flex flex-col gap-xs p-s">
              <SocialMediaShareButton.Large
                label="Facebook"
                href={getFacebookUrl({
                  shareTitle,
                  shareUrl,
                })}
                icon={<Icon size="small" SvgIcon={FacebookIcon} />}
              />

              <SocialMediaShareButton.Large
                label="Twitter"
                href={getTwitterUrl({
                  shareTitle,
                  shareUrl,
                })}
                icon={<Icon size="small" SvgIcon={TwitterIcon} />}
              />

              <SocialMediaShareButton.Large
                label="LinkedIn"
                href={getLinkedInUrl({
                  shareTitle,
                  shareUrl,
                })}
                icon={<Icon size="small" SvgIcon={LinkedInIcon} />}
              />

              <SocialMediaShareButton.Large
                label="Email"
                href={getShareEmailUrl({ shareTitle, shareUrl })}
                icon={<Icon size="small" SvgIcon={EmailIcon} />}
              />
            </div>
          </div>
        </FocusLock>
      </Flyout>
    </>
  );
};
